import { makeStyles } from '@material-ui/core/styles'
import FontAwesomeIcon from 'pf9-ui-components/built/components/FontAwesomeIcon'
import Tab from 'pf9-ui-components/built/elements/tabs/Tab'
import Tabs from 'pf9-ui-components/built/elements/tabs/Tabs'
import Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import { pipe, prop, uniqBy } from 'ramda'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import ApiClient from 'api-client/ApiClient'
import { groupByRegion, setActiveRegion } from 'api-client/helpers'
import { opencloudDashboardUrl } from 'app/constants'
import { listRegions } from 'app/plugins/infrastructure/components/regions/actions'
import { regionsSelector } from 'app/plugins/infrastructure/components/regions/selectors'
import { RootState } from 'app/store'
import DocumentMeta from 'core/components/DocumentMeta'
import AsyncDropdown from 'core/elements/dropdown/AsyncDropdown'
import { DropdownItemSpec } from 'core/elements/dropdown/DropdownCommonProps'
import { redirectToRegionLogin } from 'core/elements/header/GlobalRegionDropdown'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { isDevelopment } from 'core/utils/helpers'
import { isAdminRole } from 'core/utils/rbac'
import { routeManager } from 'core/utils/routes'
import { projectAs } from 'utils/fp'
import { listRegionsMetadata } from '../regions-metadata/actions'
import { regionsMetadataSelector } from '../regions-metadata/selectors'
import FilterToolbar from './FilterToolbar'
import { search } from './helpers'
import RegionCard from './RegionCard'
import RegionsListTable from './RegionsListTable'

export const ALERTS = {
  'Region-1': [
    {
      id: '1',
      timestamp: '2025-07-29T03:12:45Z',
      severity: 'Critical',
      title: 'Load Balancer Capacity Exceeded',
      description: 'Load balancer capacity is low',
    },
    {
      id: '2',
      timestamp: '2025-07-29T11:47:30Z',
      severity: 'Warning',
      title: 'Reserved IP Addresses',
      description: 'Reserved IP addresses are low',
    },
    {
      id: '3',
      timestamp: '2025-07-29T19:26:08Z',
      severity: 'Info',
      title: 'New VM Discovered',
      description: 'New VM Discovered',
    },
  ],
}

// Properties to search
const searchableKeys = ['name', 'infra.network.vendor', 'infra.network.product', 'region', 'type']

const filterBySearchTerm = (searchTerm, searchableKeys) => (regions) => {
  if (!searchTerm) return regions
  return search(searchTerm, regions, searchableKeys)
}

const filterByRegion = (selectedRegion) => (regions) => {
  if (!selectedRegion) return regions
  return regions.filter((region) => region.region === selectedRegion)
}

const filterByType = (selectedType) => (regions) => {
  if (!selectedType) return regions
  return regions.filter((region) => region.type === selectedType)
}

enum RegionsView {
  Cards = 'Cards',
  Table = 'Table',
}

const PLATFORM_TYPE_OPTIONS = [
  { label: 'VMware', value: 'vmware' },
  { label: 'Openstack', value: 'openstack' },
]

export default function HomePage() {
  const classes = useStyles()
  const { serviceCatalog } = ApiClient.getInstance()
  const { history } = useReactRouter()
  const [activeTab, setActiveTab] = React.useState(RegionsView.Cards)
  const { prefs, updatePrefs } = useScopedPreferences()
  const { currentRegion } = prefs
  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const isAdmin = isAdminRole(session)
  const { activeRegion } = session

  // Search and Filter
  const [searchTerm, setSearchTerm] = React.useState('')
  const [selectedRegion, setSelectedRegion] = React.useState(null)
  const [selectedType, setSelectedType] = React.useState(null)

  // Load regions
  const { loading: loadingRegions } = useListAction(listRegions)
  const regions = useSelector(regionsSelector)

  // Load regions metadata
  const { loading: loadingRegionsMetadata } = useListAction(listRegionsMetadata)
  const regionsWithMetadata = useSelector(regionsMetadataSelector)

  const filteredRegions = useMemo(() => {
    if (!regionsWithMetadata?.length) return regionsWithMetadata

    return pipe(
      filterBySearchTerm(searchTerm, searchableKeys),
      filterByRegion(selectedRegion),
      filterByType(selectedType),
    )(regionsWithMetadata)
  }, [regionsWithMetadata, searchTerm, selectedRegion, selectedType])

  // Region Options
  const regionOptions: DropdownItemSpec<any>[] = useMemo(
    () =>
      uniqBy(
        (item) => item.value,
        projectAs({ label: 'region', value: 'region' }, filteredRegions ?? []),
      ),
    [filteredRegions],
  )

  const filters = (
    <>
      <AsyncDropdown
        onChange={setSelectedRegion}
        items={regionOptions}
        placeholder="Region"
        initialValue={selectedRegion}
        value={selectedRegion}
        compact={false}
        showClearButton
      />
      <AsyncDropdown
        onChange={setSelectedType}
        items={PLATFORM_TYPE_OPTIONS}
        placeholder="Platform"
        initialValue={selectedType}
        value={selectedType}
        compact={false}
        showClearButton
      />
    </>
  )

  // Get region endpoints
  const regionEndpoints = useMemo(() => {
    const servicesByRegion = groupByRegion(serviceCatalog)
    const regionKeys = Object.keys(servicesByRegion)
    return regionKeys.reduce((accum, region) => {
      const resmgrEndpoint = servicesByRegion?.[region]?.resmgr?.public?.url
      const baseUrl = resmgrEndpoint?.split('/resmgr')?.[0]
      return {
        ...accum,
        [region]: baseUrl,
      }
    }, {})
  }, [serviceCatalog])

  useEffect(() => {
    if (regionsWithMetadata.length > 5) {
      setActiveTab(RegionsView.Table)
    }
  }, [regionsWithMetadata])

  const redirectToDashboardPage = () => history.push(routeManager.routes.openstack.dashboard.path())

  const updateRegion = useCallback(
    async (regionName, redirectPath) => {
      try {
        updatePrefs({ currentRegion: regionName })
        const redirectUrl = isDevelopment() ? window.location.origin : regionEndpoints[regionName]
        redirectToRegionLogin(redirectUrl, session, regionName, redirectPath)
        await setActiveRegion(regionName)
      } catch (err) {
        console.error(err)
      }
    },
    [regionEndpoints, session],
  )

  const handleRegionSelect = useCallback(
    async (regionName, redirectPath = opencloudDashboardUrl) => {
      if (!regionName) return
      if (regionName === currentRegion && regionName === activeRegion) {
        return redirectToDashboardPage()
      }
      updateRegion(regionName, redirectPath)
    },
    [currentRegion, activeRegion],
  )

  const handleViewAdminConsoleClick = useCallback(
    async (regionName) => {
      if (regionName === currentRegion && regionName === activeRegion) {
        return history.push(routeManager.routes.opencloud.adminConsole.path({ region: regionName }))
      }
      const redirectPath = routeManager.routes.opencloud.adminConsole.path({ region: regionName })
      updateRegion(regionName, redirectPath)
    },
    [currentRegion, activeRegion],
  )

  const filteredRegionsWithExtraMetadata = useMemo(() => {
    return filteredRegions.map((region) => {
      return {
        ...region,
        alerts: ALERTS[region.name],
      }
    })
  }, [filteredRegions])

  return (
    <>
      <DocumentMeta title="Home" breadcrumbs />
      <div>
        <div className={classes.title}>
          <FontAwesomeIcon size="xl">cloud</FontAwesomeIcon>
          <Text>Your Clusters</Text>
        </div>
        <Tabs activeTab={activeTab} setActiveTab={setActiveTab}>
          <Tab label="Card view" value={RegionsView.Cards}>
            <FilterToolbar
              className={classes.toolbar}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              filters={filters}
            />
            <div className={classes.cardContainer}>
              {filteredRegionsWithExtraMetadata.map((region) => (
                <RegionCard
                  key={region.name}
                  region={region}
                  alerts={ALERTS[region.name]}
                  onClick={() => handleRegionSelect(region.name)}
                  onViewAdminConsoleClick={() => handleViewAdminConsoleClick(region.name)}
                  isAdmin={isAdmin}
                />
              ))}
            </div>
          </Tab>
          <Tab label="Table view" value={RegionsView.Table}>
            <FilterToolbar
              className={classes.toolbar}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              filters={filters}
            />
            <RegionsListTable
              data={filteredRegionsWithExtraMetadata}
              onRegionSelect={handleRegionSelect}
            />
          </Tab>
        </Tabs>
      </div>
    </>
  )
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  title: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, max-content)',
    gridGap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(4),
    '& p': {
      fontSize: '32px',
    },
    marginTop: theme.spacing(2),
  },
  cardContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 400px))',
    gridGap: theme.spacing(2),
  },
  toolbar: {
    display: 'grid',
    gridTemplateColumns: 'minmax(200px, 400px) max-content',
    gridGap: theme.spacing(2),
    justifyContent: 'start',
    alignItems: 'end',
    '& .controls': {
      width: '100%',
    },
    '& .filters': {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(100px, 200px))',
      gridGap: theme.spacing(2),
    },
  },
}))
